/**
 * BalaoAjuda — botão "?" que abre um balão de explicação.
 *
 * É a casca comum do "?" do sistema: o ExplicacaoCard (cards de painel) e a
 * ajuda de cada permissão na Gestão de Acessos usam esta mesma peça e só
 * mudam o conteúdo (`children`).
 *
 * Diretrizes (vieram do painel do PharuCut e valem para qualquer "?"):
 * - Abre no clique/toque, nunca no hover: no tablet não existe "passar o
 *   mouse", e uma dica que some não dá tempo de ler o exemplo.
 * - Fecha no ×, no Esc, clicando fora e rolando a página.
 * - O balão vai para o <body> (portal), não fica dentro do card: a borda do
 *   card cortaria o texto. Abre abaixo do "?", alinhado à direita, 360px,
 *   sem passar da janela e com rolagem própria.
 * - Clicar no "?" não dispara outra ação de quem está por baixo (arrastar o
 *   card, marcar a permissão).
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";

import "../ExplicacaoCard/explicacao-card.css";

const LARGURA_BALAO = 360;
const MARGEM_JANELA = 8;
const DISTANCIA_DO_BOTAO = 6;
// Abaixo disso o balão sobe um pouco em vez de virar uma fresta ilegível.
const ALTURA_MINIMA = 160;

// Posição do balão: abaixo do "?", borda direita alinhada à do botão, presa
// dentro da janela nos dois eixos. Função pura para poder ser testada.
export function posicionarBalao(ancora, janela) {
  const largura = Math.min(LARGURA_BALAO, janela.largura - MARGEM_JANELA * 2);
  const esquerdaIdeal = ancora.right - largura;
  const left = Math.max(
    MARGEM_JANELA,
    Math.min(esquerdaIdeal, janela.largura - largura - MARGEM_JANELA)
  );
  const top = Math.max(
    MARGEM_JANELA,
    Math.min(
      ancora.bottom + DISTANCIA_DO_BOTAO,
      janela.altura - ALTURA_MINIMA - MARGEM_JANELA
    )
  );
  const alturaMaxima = janela.altura - top - MARGEM_JANELA;
  return { top, left, largura, alturaMaxima };
}

// Impede que o toque no "?" vire arrasto/clique de quem está por baixo.
const pararPropagacao = (e) => e.stopPropagation();

/**
 * @param {string} titulo           título no topo do balão
 * @param {string} rotuloGatilho    aria-label do "?" (ex.: "O que é: Refugo")
 * @param {string} rotuloBalao      aria-label do balão (ex.: "Como ler: Refugo")
 * @param {string} dica             title do "?" (texto do tooltip nativo)
 * @param {string} className        classe extra no "?" (variações de tamanho)
 */
function BalaoAjuda({ titulo, rotuloGatilho, rotuloBalao, dica, className, children }) {
  const [aberto, setAberto] = useState(false);
  const [posicao, setPosicao] = useState(null);
  const botaoRef = useRef(null);
  const balaoRef = useRef(null);
  const idBalao = useId();

  const fechar = useCallback(() => setAberto(false), []);

  const medir = useCallback(() => {
    const botao = botaoRef.current;
    if (!botao) return;
    const r = botao.getBoundingClientRect();
    setPosicao(
      posicionarBalao(
        { right: r.right, bottom: r.bottom },
        { largura: window.innerWidth, altura: window.innerHeight }
      )
    );
  }, []);

  useLayoutEffect(() => {
    if (aberto) medir();
  }, [aberto, medir]);

  useEffect(() => {
    if (!aberto) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        fechar();
        botaoRef.current?.focus();
      }
    };
    const onPointerDown = (e) => {
      if (balaoRef.current?.contains(e.target)) return;
      if (botaoRef.current?.contains(e.target)) return;
      fechar();
    };
    // Rolar a PÁGINA fecha; rolar o texto dentro do balão, não.
    const onScroll = (e) => {
      if (balaoRef.current && e.target instanceof Node && balaoRef.current.contains(e.target)) {
        return;
      }
      fechar();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", fechar);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", fechar);
    };
  }, [aberto, fechar]);

  const alternar = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAberto((v) => !v);
  };

  const balao =
    aberto && posicao
      ? createPortal(
          <div
            ref={balaoRef}
            id={idBalao}
            role="dialog"
            aria-label={rotuloBalao}
            className="explicacao-card"
            style={{
              top: posicao.top,
              left: posicao.left,
              width: posicao.largura,
              maxHeight: posicao.alturaMaxima,
            }}
            onClick={pararPropagacao}
            onPointerDown={pararPropagacao}
          >
            <div className="explicacao-card__topo">
              <strong className="explicacao-card__titulo">{titulo}</strong>
              <button
                type="button"
                className="explicacao-card__fechar"
                aria-label="Fechar explicação"
                onClick={fechar}
              >
                <FiX size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="explicacao-card__corpo">{children}</div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={botaoRef}
        type="button"
        className={["explicacao-card__gatilho", className].filter(Boolean).join(" ")}
        aria-label={rotuloGatilho}
        aria-expanded={aberto}
        aria-controls={aberto ? idBalao : undefined}
        title={dica}
        draggable={false}
        onClick={alternar}
        onPointerDown={pararPropagacao}
        onMouseDown={pararPropagacao}
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        ?
      </button>
      {balao}
    </>
  );
}

export default BalaoAjuda;
